/**
 * Authentication service contract.
 *
 * Extracted into its own module so an implementation and its consumers can
 * each depend on the interface without importing one another (avoids circular
 * imports through the instance exported from `authService.ts`).
 *
 * @module features/auth/services/authService.types
 */

import type {
  AuthResponse,
  OAuthProvider,
  RegisterData,
  User,
} from '../types/auth.types';

/**
 * Thrown by {@link IAuthService.changePassword} when the supplied current
 * password does not match the signed-in user's actual password. Lets the UI
 * distinguish a wrong current password from other failures.
 */
export class InvalidCurrentPasswordError extends Error {
  constructor(message = 'Текущата парола е грешна.') {
    super(message);
    this.name = 'InvalidCurrentPasswordError';
  }
}

/**
 * Authentication service interface.
 * Defines all authentication operations for the application.
 */
export interface IAuthService {
  login(username: string, password: string): Promise<AuthResponse>;
  register(data: RegisterData): Promise<AuthResponse>;
  logout(): Promise<void>;
  refreshToken(token: string): Promise<AuthResponse>;
  getCurrentUser(): Promise<User>;
  verifyToken(token: string): Promise<boolean>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  forgotPassword(username: string): Promise<void>;
  /**
   * Apply a new password to the recovery session the emailed link already
   * established (PKCE — there is no token left for the app to exchange).
   */
  resetPassword(newPassword: string): Promise<void>;
  parentChangePassword(userId: string, newPassword: string): Promise<void>;
  /**
   * Persist the current user's avatar URL (stored in Supabase auth metadata)
   * and return the updated {@link User}.
   */
  updateAvatar(avatarUrl: string): Promise<User>;
  /**
   * Begin a third-party OAuth sign-in flow. In Supabase mode this triggers a
   * full-page redirect to the provider; the promise typically does not resolve
   * before the navigation occurs.
   */
  signInWithOAuth(provider: OAuthProvider): Promise<void>;
  /**
   * Complete an OAuth session once the user returns to the app callback route.
   * The SDK exchanges the PKCE code (via `detectSessionInUrl`); this reads the
   * resulting session and maps it into an {@link AuthResponse}.
   */
  completeOAuthSession(): Promise<AuthResponse>;
  /**
   * Finalise an email-invited account on `/accept-invite`: the invite link
   * already established the session and the signup trigger provisioned the
   * profile + membership — this sets the user's chosen name, username and
   * password.
   */
  completeInvitedAccount(data: {
    readonly name: string;
    readonly username: string;
    readonly password: string;
  }): Promise<AuthResponse>;
}
